import { ReactElement, ReactNode, SyntheticEvent } from 'react';
import classNames from 'classnames';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Grid, Tooltip } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { IHospitalDetails, IHospitalHistoryItem } from '~/services/api/hospital/types';
import { getEventMetadata } from '~/ui/pages/Hospital/DetailsModal/utils';

import styles from './Item.module.scss';
import { DATE_TIME_FORMAT, EMPTY_VALUE } from '../../../DetailsModal/constants';
import { isoToFormat } from '~/utils/date';

interface IProps {
  item: IHospitalHistoryItem;
  historyDetails: IHospitalDetails | null;
  isExpanded: boolean;
  onAccordionChange: (item: IHospitalHistoryItem, isExpanded: boolean) => void;
  children: ReactNode;
}

const Item = ({
  item,
  historyDetails,
  isExpanded,
  onAccordionChange,
  children,
}: IProps): ReactElement => {
  const { eventThemeClass } = getEventMetadata(historyDetails?.eventId || item.eventId);

  const formattedAdmitDateTime = item.admitDateTime
    ? isoToFormat(item.admitDateTime, DATE_TIME_FORMAT, item.patientTimezone)
    : EMPTY_VALUE;

  const receivedBySystem = `Received by system: ${isoToFormat(
    item.processedAt,
    DATE_TIME_FORMAT,
    item.patientTimezone,
  )}`;

  const handleAccordionChange = (_event: SyntheticEvent, nextExpanded: boolean) => {
    onAccordionChange(item, nextExpanded);
  };

  return (
    <Accordion
      expanded={isExpanded}
      onChange={handleAccordionChange}
      className={classNames(
        styles.accordion,
        styles[eventThemeClass],
        isExpanded && styles.expanded,
      )}
      disableGutters
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon className={styles.expandIcon} />}
        className={styles.summary}
        classes={{
          content: styles.summaryMuiContent,
          expandIconWrapper: styles.summaryExpandIconWrapper,
        }}
      >
        <Grid container gap={2} className={styles.summaryContent}>
          <Grid item>
            <span
              title={receivedBySystem}
              className={classNames(styles.summaryTag, styles.summaryEventTag)}
            >
              {isoToFormat(item.messageTimestamp, DATE_TIME_FORMAT, item.patientTimezone)}
            </span>
          </Grid>

          <Grid item xs={3.5}>
            <span className={classNames(styles.summaryTag, styles.summaryEventTag)}>
              {item.eventName}: {formattedAdmitDateTime}
            </span>
          </Grid>

          <Grid item xs={0.5} className={styles.summaryArrowItem}>
            <ArrowForwardIcon className={styles.summaryArrowIcon} />
          </Grid>

          <Grid item xs className={styles.summaryItem}>
            <Tooltip title={item.facility || EMPTY_VALUE} placement="top">
              <span className={classNames(styles.summaryTag, styles.summarySecondaryTag)}>
                {`Facility: ${item.facility || '-'}`}
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails className={styles.accordionDetails}>{children}</AccordionDetails>
    </Accordion>
  );
};

export default Item;
